//core
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form, FormikHelpers, FormikValues, FormikProps } from 'formik';
import { useSelector } from "react-redux";
import { Alert, Box, IconButton, Typography, useMediaQuery } from "@mui/material";
import format from "date-fns/format";
import { getSymbolFromCode } from "currency-code-symbol-map";

//styles
import { useStyles } from "./styles";

//selectors
import {
	selectTrackersData,
	selectTrackersIsFetching,
	selectBrandsData,
	selectBrandsIsFetching,
} from "../../../../../store/common/fields/selectors";
import { selectAffiliatesData, selectAffiliatesIsFetching } from "../../../../../store/admin/partners/selectors";
import {
	selectCreateData,
	selectCreateError,
	selectUpdateData,
	selectUpdateError
} from "../../../../../store/admin/flatFee/selectors";

//hooks
import { useFlatFee } from "../../../../../store/admin/flatFee/useFlatFee";
import { usePartners } from "../../../../../store/admin/partners/usePartners";
import { useFields } from "../../../../../store/common/fields/useFields";
import { getObjIds } from "../../../../../helpers/getIds";
import { useConfiguration } from "../../../../../store/common/configuration/useConfiguration";
import { normalizeNumber } from "../../../../../helpers/utils";
import { selectUploadConfigData } from "../../../../../store/common/configuration/selectors";
import { useSummaryReport } from "../../../../../store/admin/reports/summary_report_v2/useSummaryReport";
import {
	selectCurrenciesData,
	selectCurrenciesIsFetching,
} from "../../../../../store/admin/reports/summary_report_v2/selectors";

//validation
import { validationTextField as validationTextFieldMax } from "../../../../validation/textFieldMax.validation";
import { validationAmountField } from "../../../../validation/amount.validation";
import { validationRequiredAutocompleteField } from "../../../../validation/requiredAutocompleteStringId.validation";
import { validationDate } from "../../../../validation/date.validation";

//constants
import { paymentsTypeOptions } from "../../constants";

//components
import HelperField from "./HelperField";
import MuiTextField from "../../../../UI/FormFields/MuiTextField";
import Button from "../../../../UI/Buttons/Button";
import MuiAutocompleteSelect from "../../../../UI/FormFields/MuiAutocompleteSelect";
import { MuiSingleDatePicker } from "../../../../UI/FormFields/MuiSingleDatePicker";
import { MuiSelect } from "../../../../UI/FormFields/MuiSelect";
import MuiStaticAutocomplete from "../../../../UI/FormFields/MuiStaticAutocomplete";

const exceptThisSymbols = ["e", "E", "+", "-", ","];

export interface ICreateUpdateFlatFeeProps {
  closeModal?: (state:boolean) => void;
  data?: { [key: string]: any; };
	handleSubmitForm: any;
	title: string;
	affiliate?: { id: number | undefined; value: string | undefined; };
	tracker?: { id: number | undefined; value: string | undefined; };
}

const CreateUpdateFlatFee: React.FC<ICreateUpdateFlatFeeProps> = ({
  closeModal,
  data,
  handleSubmitForm,
  title,
  affiliate,
	tracker,
}): JSX.Element => {
  const styles = useStyles();
  const { t, i18n } = useTranslation();
  const mobile = useMediaQuery('(max-width:767px)');
  const formRef = useRef<FormikProps<any>>(null);

  const { clearUpdateFlatFee, clearCreateFlatFee } = useFlatFee();
	const { getAffiliates } = usePartners();
	const { getTrackers, clearTrackers,
		// getAffiliates,
		getBrands } = useFields();
	const { getUploadConf } = useConfiguration();
	const { getCurrencies, clearCurrencies } = useSummaryReport();

	const affiliateIdData = useSelector(selectAffiliatesData);
	const affiliateIdFetching = useSelector(selectAffiliatesIsFetching);
	const trackerIdData = useSelector(selectTrackersData);
	const trackerIdFetching = useSelector(selectTrackersIsFetching);
	const brandIdData = useSelector(selectBrandsData);
	const brandIdFetching = useSelector(selectBrandsIsFetching);
	const currenciesData = useSelector(selectCurrenciesData);
	const currenciesFetching = useSelector(selectCurrenciesIsFetching);
	const createData = useSelector(selectCreateData);
	const createError = useSelector(selectCreateError);
	const updateData = useSelector(selectUpdateData);
	const updateError = useSelector(selectUpdateError);
	const uploadConfigData = useSelector(selectUploadConfigData);

  const [initialValues, setInitialValues] = useState<any | null>(null);

  //set initial form value
  useEffect(() => {
    setInitialValues({
      affiliateId: !!data ? {id: data.affiliate_id, value: data.affiliate_name} : affiliate || '',
      date: !!data ? data.target_date : '',
      trackerId: !!data ? {id: data.tracker_id, value: data.tracker_name} : tracker || '',
      brand: !!data ? {id: data.brand, value: data.brand_name} : '',
      brandCurrency: !!data && data.brand_currency ? {id: data.brand_currency, value: data.brand_currency} : '',
      amount: !!data ? normalizeNumber(data.amount) : '',
      description: !!data ? data.description : '',
	    paymentType: !!data ? {id: data.payment_type, value: data.payment_type} : paymentsTypeOptions[0],
    });
  }, [data, affiliate, tracker])

	// validation errors translation
	const [errorsMessages, setErrorsMessages] = useState({
		required: t("common.validation.required_field"),
		amountRequired: t("common.validation.required_field"),
		amountMax: t("common.validation.amount_max"),
		amountMin: t("common.validation.must_be_positive"),
		notNumber: t("common.validation.not_a_number"),
		maxLength: t("common.validation.max_length_255"),
		minLength: t("common.validation.min_length"),
	});

  useEffect(() => {
    setErrorsMessages({
      required: t("common.validation.required_field"),
      amountRequired: t("common.validation.required_field"),
      amountMax: t("common.validation.amount_max"),
      amountMin: t("common.validation.must_be_positive"),
      notNumber: t("common.validation.not_a_number"),
      maxLength: t("common.validation.max_length"),
      minLength: t("common.validation.min_length"),
    });

    setTimeout(() => {
      formRef.current?.validateForm();
    }, 50)
  }, [i18n.language, t]);

	useEffect(() => {
		if (!uploadConfigData) {
			getUploadConf();
		}
	}, [uploadConfigData]);

  useEffect(() => {
      return () => {
	      clearCreateFlatFee();
	      clearUpdateFlatFee();
	      clearTrackers();
	      clearCurrencies();
        setInitialValues({
	        affiliateId: '',
	        date: '',
	        trackerId: '',
	        brand: '',
	        brandCurrency: '',
	        amount: '',
	        description: '',
        });
      }
  }, []);

  const submitForm = (values: FormikValues, actions: FormikHelpers<any>): void => {
    const payload = {
      affiliateId: !!values.affiliateId ? getObjIds(values.affiliateId) : null,
      targetDate: !!values.date ? format(new Date(values.date), "yyyy-MM-dd") : null,
      trackerId: !!values.trackerId ? getObjIds(values.trackerId) : null,
      brandCode: !!values.brand ? getObjIds(values.brand) : null,
      brandCurrency: !!values.brandCurrency ? getObjIds(values.brandCurrency) : null,
      amount: !!values.amount ? values.amount : null,
      description: !!values.description ? values.description : null,
	    paymentType: !!values.paymentType ? getObjIds(values.paymentType) : null,
	    ...(!!data?.id ? {flatFeeId: data.id} : {}),
    }
	  handleSubmitForm(payload);
  };

	const getAffiliatesIdsOptions = (page: number, search: string | null) => {
		getAffiliates({
			page: page,
			per_page: 100,
			search: search,
			chiefIds: null,
			departmentIds: null,
			status: 'approved'
		});
	};

	const getBrandsOptions = (page: number, search: string | null) => {
		getBrands({
			page: page,
			per_page: 100,
			search: search,
		});
	};

	useEffect(() => {
		if (!!createData) {
			closeModal?.(false)
		}
	}, [createData]);

	useEffect(() => {
		if (!!updateData) {
			closeModal?.(false)
		}
	}, [updateData]);

  const validationAmount = (value: any) => validationAmountField(value, errorsMessages);
  const validateRequiredAutocompleteSelectField = (value: any) => validationRequiredAutocompleteField(value, errorsMessages);
	const validateDate = (value: any) => validationDate(value, errorsMessages);
	const validateDescription = (value: any) => validationTextFieldMax(value, 255, errorsMessages);

  return (
    <>
      {!!initialValues && (
        <Box className={styles.block}>
          <Typography className={styles.title} variant="h6">
            {t(`${title}`)}
          </Typography>
          <Typography className={styles.subtitle} variant="body2">
            {t("admin.flatFee.createUpdateForm.subtitle")}
          </Typography>

          <Box className={styles.content}>
            <Formik
              validateOnChange
              validateOnBlur
              validateOnMount
              enableReinitialize
              innerRef={formRef}
              initialValues={initialValues}
              onSubmit={ (values, actions) => submitForm(values, actions) }
            >
              { props => {
                const { values, handleSubmit, handleChange, isValid, dirty } = props;

                const getTrackerIdsOptions = (page: number, search: string | null) => {
                  getTrackers({
                    page: page,
                    per_page: 100,
	                  search: search,
	                  affiliateIds: values.affiliateId ? [Number(getObjIds(values.affiliateId))] : null,
                  });
                };

                const getBrandCurrenciesOptions = (page: number, search: string | null) => {
                  getCurrencies({
                    page: page,
                    perPage: 100,
                    search: search,
                    brandCodes: values.brand ? [getObjIds(values.brand)] : [],
                  });
                };

	              const disableSubmit = isValid ? !dirty : true;

                return (
                  <Form className={styles.form} onSubmit={handleSubmit}>
                    <Box className={styles.field}>
                      <MuiAutocompleteSelect
                        keyValue="value"
                        keyId="id"
                        id="affiliateId"
                        name="affiliateId"
                        label={t("admin.partners.form.affiliate_ids.label")}
                        value={values.affiliateId}
                        multiple={false}
                        showSelectAll={false}
                        validate={validateRequiredAutocompleteSelectField}
                        onChange={handleChange}
                        getOptions={getAffiliatesIdsOptions}
                        data={affiliateIdData}
                        isFetching={affiliateIdFetching}
                        disabled={!!data}
						isRequired={true}
                      />
                    </Box>

	                  <Box className={styles.field}>
		                  <MuiSingleDatePicker
			                  id="date"
			                  name="date"
			                  autoComplete="date"
			                  label={t("admin.reports.detailed_fin_report.payouts.form.date")}
			                  value={values.date}
			                  views={["month", "year", "day"]}
			                  validate={validateDate}
							  isRequired={true}
		                  />
	                  </Box>

	                  <Box className={styles.field}>
		                  <MuiAutocompleteSelect
			                  keyValue="value"
			                  keyId="id"
			                  id="trackerId"
			                  name="trackerId"
			                  label={t("admin.reports.filter.tracker_id")}
			                  value={values.trackerId}
			                  multiple={false}
			                  showSelectAll={false}
			                  validate={validateRequiredAutocompleteSelectField}
			                  onChange={handleChange}
			                  getOptions={getTrackerIdsOptions}
			                  data={trackerIdData}
			                  isFetching={trackerIdFetching}
			                  disabled={!values.affiliateId || !!data}
							  isRequired={true}
		                  />
	                  </Box>

	                  <Box className={styles.field}>
		                  <MuiAutocompleteSelect
			                  keyValue="value"
			                  keyId="id"
			                  id="brand"
			                  name="brand"
			                  label={t("common.forms.fields.brand")}
			                  value={values.brand}
			                  multiple={false}
			                  showSelectAll={false}
			                  validate={validateRequiredAutocompleteSelectField}
			                  onChange={handleChange}
			                  getOptions={getBrandsOptions}
			                  data={brandIdData}
			                  isFetching={brandIdFetching}
			                  disabled={!!data}
			                  labelWithId={true}
							  isRequired={true}
		                  />
	                  </Box>

	                  {!!values.brand && (
		                  <Box className={styles.field}>
			                  <MuiAutocompleteSelect
				                  keyValue="value"
				                  keyId="id"
				                  id="brandCurrency"
				                  name="brandCurrency"
				                  label={t(`admin.flatFee.form.brandCurrency`)}
				                  value={values.brandCurrency}
				                  onChange={handleChange}
				                  getOptions={getBrandCurrenciesOptions}
				                  data={currenciesData}
				                  isFetching={currenciesFetching}
			                  />
		                  </Box>
	                  )}

                    <Box className={styles.field}>
                      <MuiTextField
                        size="small"
                        id="amount"
                        type="number"
                        name="amount"
                        autoComplete="amount"
                        label={t("common.forms.fields.amount")}
                        value={values.amount}
                        validate={(value) => validationAmount(value)}
                        onChange={handleChange}
												isRequired={true}
                      />
                    </Box>

	                  <Box className={styles.field}>
		                  <MuiStaticAutocomplete
		                    keyValue="value"
		                    keyId="id"
		                    id="paymentType"
		                    name="paymentType"
		                    label={t("common.forms.fields.payment_type")}
		                    value={values.paymentType}
		                    multiple={false}
		                    showSelectAll={false}
		                    staticOptions={paymentsTypeOptions}
		                    validate={validateRequiredAutocompleteSelectField}
		                    onChange={handleChange}
		                    isRequired={true}
		                    disabled={true}
		                    prefix="admin.flatFee.form.options.paymentTypes."
		                  />
	                  </Box>

                    <Box className={styles.field}>
                      <MuiTextField
                        size="small"
                        id="description"
                        type="text"
                        name="description"
                        autoComplete="description"
                        label={t("common.forms.description")}
                        value={values.description}
                        onChange={handleChange}
                        validate={validateDescription}
                        multiline={false}
						isRequired={true}
                      />
                    </Box>

                    <HelperField
                      values={values}
                    />

	                  {!!createError && !!createError.error && (
		                  <Alert sx={{ marginBottom: 1 }} variant="outlined" severity="error">{t(`common.errors.flatFee.createUpdate.${createError.error}`)}</Alert>
	                  )}
	                  {!!updateError && !!updateError.error && (
		                  <Alert sx={{ marginBottom: 1 }} variant="outlined" severity="error">{t(`common.errors.flatFee.createUpdate.${updateError.error}`)}</Alert>
	                  )}

                    <Box className={styles.footer}>
                      <Box className={styles.actions}>
	                      <Button
		                      gradient
		                      fullWidth
		                      disableElevation
		                      type="submit"
		                      variant="contained"
		                      className={styles.btn}
		                      disabled={disableSubmit}
	                      >{t("common.buttons.save")}</Button>
	                      <Button
		                      fullWidth
		                      disableElevation
		                      type="button"
		                      variant="outlined"
		                      color="secondary"
		                      className={styles.btn}
		                      onClick={() => closeModal?.(false)}
	                      >{t("common.buttons.cancel")}</Button>
                      </Box>
                    </Box>
                  </Form>
                )
              }}
            </Formik>
          </Box>
        </Box>
      )}
    </>
  );
};

export default CreateUpdateFlatFee;
